import {
  ILanguage,
  SellingMode,
} from "@/api/AppApi/services/company/interfaces/ICompany";
import { MutableRefObject } from "react";
import moment from "moment-timezone";
export const a2e_num = (str = "") =>
  str.replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d)?.toString());

export const sortArray = (arrOfObjects: any[], sortBy = "sortable") => {
  if (arrOfObjects && arrOfObjects.length > 0)
    return arrOfObjects.sort((a, b) => a[sortBy] - b[sortBy]);

  return [];
};

export const stringBoolean = (someVariable: any) =>
  someVariable === true || someVariable === "1";

export const truncateString = (str: string = "", n = 20) => {
  if (str?.length <= n) {
    return str;
  }
  const subString = str?.substring(0, n - 1);
  let lastSpaceIndex = subString?.lastIndexOf(" ");
  if (lastSpaceIndex < 0) lastSpaceIndex = n - 1;
  return subString?.substring(0, lastSpaceIndex) + "…";
};
export const numAsString = (x: number) =>
  x < 1000
    ? `${x}`
    : x < 1e6
    ? (x / 1000).toFixed(1) + "k"
    : (x / 1e6).toFixed(2) + "M";
export const stripHtml = (str: string) => str.replace(/<[^>]+>/g, "");

export const direction = (language: string, languages: ILanguage[]) => {
  const index = languages?.findIndex((lang) => lang.code === language);
  if (index < 0) return "ltr";
  return languages[index].is_rtl ? "rtl" : "ltr";
};

export const getRndInteger = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;
export const getRandom = () => {
  const date = new Date();
  return (
    getRndInteger(1e6, 1e7) +
    (date.getSeconds() + date.getMilliseconds())?.toString()
  );
};
export const hexToRGBA = (hex: string, opacity: number) => {
  if (!hex.startsWith("#")) return hex.replace(",1)", `,${opacity})`);
  else
    return (
      "rgba(" +
      (hex = hex.replace("#", ""))
        .match(new RegExp("(.{" + hex.length / 3 + "})", "g"))
        ?.map(function (l) {
          return parseInt(hex.length % 2 ? l + l : l, 16);
        })
        .concat(isFinite(opacity) ? opacity : 1)
        .join(",") +
      ")"
    );
};

export const getSliderScrollIndex = (
  sliderWidth: number,
  widthArr: number[],
  selectedIndex: number,
) => {
  let sum = 0;
  let index: number;
  for (index = widthArr.length - 1; index >= 0; index--) {
    sum += widthArr[index];
    if (sum >= sliderWidth) break;
  }
  return selectedIndex > index ? index + 1 : selectedIndex;
};
export const formatServerDateTime = (date: Date) => {
  if (!date || date?.toString() === "") return;
  let year = date.getFullYear();
  let month = (date.getMonth() + 1)?.toString().padStart(2, "0");
  let day = date.getDate()?.toString().padStart(2, "0");
  let hours = date.getHours()?.toString().padStart(2, "0");
  let minutes = date.getMinutes()?.toString().padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};
export const getWidthFromDivElement = (
  divRef: MutableRefObject<HTMLDivElement | null>,
) => (divRef?.current?.offsetWidth ?? 1) - 0.1;

export const getDivProperties = (
  divRef: MutableRefObject<HTMLDivElement>,
): Partial<HTMLDivElement> | undefined => {
  if (divRef && divRef?.current) {
    const {
      clientHeight,
      clientWidth,
      offsetHeight,
      offsetWidth,
      className,
      id,
      style,
    } = divRef.current;
    return {
      clientHeight,
      clientWidth,
      offsetHeight,
      offsetWidth,
      className,
      id,
      style,
    };
  }
  return {
    clientHeight: 0,
    clientWidth: 0,
    offsetHeight: 0,
    offsetWidth: 0,
    className: "",
    id: "",
  };
};

export const intDivision = (a: number, b: number) => {
  const r = a % b;
  const q = (a - r) / b;
  return { q, r };
};
export const carouselSpeed = (interval: string | number | undefined) => {
  return interval && interval !== "" ? Math.max(+interval, 2000) : 0;
};

export const downloadExcelFile = (csvData: any, fileName: string) => {
  const blob = new Blob([csvData], { type: "application/octet-stream" });
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(blobUrl);
};

export function cleanObject<T extends Record<string, any>>(obj: T): Partial<T> {
  const newObj: Partial<T> = {};

  for (const key in obj) {
    if (Object.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== null && value !== undefined && value !== "") {
        newObj[key] = value;
      }
    }
  }

  return newObj;
}

// Function to get time zones by ISO 3166-1 alpha-2 country code
function getTimeZonesByCountryCode(countryCode: string): string[] | null {
  const upperCaseCountryCode = countryCode.toUpperCase();
  const timeZones = moment.tz.zonesForCountry(upperCaseCountryCode);
  return timeZones && timeZones.length > 0 ? timeZones : null;
}

// Modified isOpenNow to handle time zones
function isOpenNow(
  open_at?: string | null,
  close_at?: string | null,
  timeZone?: string,
): boolean {
  // If no open_at or close_at, assume open
  if (!open_at || !close_at) {
    return true;
  }

  // If no time zone provided, default to UTC
  const selectedTimeZone = timeZone || "UTC";

  // Handle special case for 24:00 or 00:00
  const adjustedCloseAt =
    close_at === "24:00" || close_at === "00:00" ? "23:59" : close_at;

  const now = moment().tz(selectedTimeZone);

  const openTime = moment.tz(open_at, "HH:mm", selectedTimeZone);
  const closeTime = moment.tz(adjustedCloseAt, "HH:mm", selectedTimeZone);

  if (closeTime.isBefore(openTime)) {
    closeTime.add(1, "day");
  }

  return now.isBetween(openTime, closeTime, undefined, "[]");
}

export function checkSellingAvailability(
  sellingMode: SellingMode | undefined,
  enable_sells?: boolean,
  open_at?: string | null,
  close_at?: string | null,
  countryCode: string = "KW",
): boolean {
  let timeZone: string | undefined;
  if (countryCode) {
    const timeZones = getTimeZonesByCountryCode(countryCode);
    timeZone = timeZones ? timeZones[0] : "Asia/Kuwait";
  }
  if (sellingMode) {
    if (sellingMode === SellingMode.disableSells) {
      return false;
    }
    return isOpenNow(open_at, close_at, timeZone);
  }
  return enable_sells ? isOpenNow(open_at, close_at, timeZone) : false;
}

export function roundToFourDecimals(num: number): number {
    if (Number.isInteger(num)) {
        return num;
    }
    return Number(num.toFixed(4));
}

type SearchParamsValue = string | number | boolean | string[] | number[];

interface SearchParams {
  [key: string]: SearchParamsValue | null | undefined;
}

export function convertToUrlSearchParams(searchParams: SearchParams): string {
  if (!searchParams) return '';

  const params = new URLSearchParams();
  
  Object.entries(searchParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        value.forEach(item => params.append(key, String(item)));
      } else {
        params.append(key, String(value));
      }
    }
  });

  return params.toString();
}


export function convertToQueryString(searchParams: SearchParams): string {
  const paramString = convertToUrlSearchParams(searchParams);
  return paramString ? `?${paramString}` : '';
}
